import { useCallback } from 'react'

import type { InfiniteData } from '@tanstack/react-query'

import { describeEditors } from '@/components/files/FileEditors/describeEditors'

import { useFileEditors } from '@/hooks/files/useFileEditors'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { User, UserID } from '@/types/User'

interface UseEditorsInRoomArgs {
  fileId: UUID
  enabled?: boolean
}

export interface EditorsInRoom {
  /** The people in the room this browser can put a name to. */
  users: User[]
  /** In the room but unnamed here: another organization's member, or a client that never said who it was. */
  unnamed: number
  /** Who is editing, as a sentence. */
  description: string
  /** The document those people are in. */
  href: string
}

const fullName = (user: User): string =>
  `${user.first_name} ${user.last_name}`.trim()

/**
 * Whoever has this file open in the editor, named, and where they are. Null
 * where nobody is in the room, which is nearly every file nearly all the time.
 *
 * Which document the room belongs to comes back with the presence rather than
 * being looked up from the file, so following it lands on the copy being edited
 * even in the moment a file has two.
 */
export const useEditorsInRoom = ({
  fileId,
  enabled = true,
}: UseEditorsInRoomArgs): EditorsInRoom | null => {
  const { data: presence } = useFileEditors({ fileId, enabled })
  const { activeOrgId } = useActiveOrgId()

  const userIds = presence?.user_ids
  const selectEditors = useCallback(
    (
      roster: InfiniteData<PaginatedResponse<User>, PaginateRequest>,
    ): User[] => {
      if (!userIds?.length) {
        return []
      }
      const inRoom = new Set<UserID>(userIds)
      return roster.pages
        .flatMap((page) => page.data)
        .filter((user) => inRoom.has(user.id))
    },
    [userIds],
  )
  // The roster is one query the rest of the app has already asked for.
  const { data: editors } = useOrganizationUsers({
    orgId: activeOrgId,
    select: selectEditors,
    enabled: !!userIds?.length,
  })

  if (!presence || !activeOrgId) {
    return null
  }

  // Somebody the roster has no record of is still somebody in the room.
  const users = editors ?? []
  const unnamed = Math.max(presence.user_ids.length - users.length, 0)

  return {
    users,
    unnamed,
    description: describeEditors({ names: users.map(fullName), unnamed }),
    href: `/orgs/${activeOrgId}/pursuits/${presence.pursuit_id}/documents/${presence.document_id}`,
  }
}
